/**
 * Campo "Funções" do modal de contato — GUPM-202. As funções são etiquetas:
 * digita (com sugestões das funções já usadas, `<datalist>`), Enter cria a
 * etiqueta, "×" tira. O texto digitado e ainda sem Enter também conta ao salvar
 * (o formulário o junta às etiquetas). Só apresentação.
 */
import type { KeyboardEvent } from 'react';

interface Props {
  funcoes: readonly string[];
  digitada: string;
  sugestoes: readonly string[];
  erro?: string;
  onDigitar: (texto: string) => void;
  onAdicionar: () => void;
  onRemover: (funcao: string) => void;
}

const ID = 'contato-funcoes';

export default function CampoFuncoes({ funcoes, digitada, sugestoes, erro, onDigitar, onAdicionar, onRemover }: Props) {
  const escolhidas = new Set(funcoes.map((f) => f.toLocaleLowerCase('pt-BR')));
  function aoTeclar(e: KeyboardEvent<HTMLInputElement>) {
    // Enter cria a etiqueta — não envia o formulário.
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdicionar();
    }
  }
  return (
    <div>
      <label htmlFor={ID} className="block text-[11px] font-bold text-jf-muted">Funções</label>
      {funcoes.length > 0 && (
        <ul className="mt-1 flex flex-wrap gap-1" aria-label="Funções escolhidas">
          {funcoes.map((f) => (
            <li key={f} className="inline-flex items-center gap-1 rounded border border-jf-border bg-white/5 py-0.5 pl-1.5 pr-0.5 text-[11px] text-white">
              <span>{f}</span>
              <button
                type="button"
                onClick={() => onRemover(f)}
                aria-label={`Remover função ${f}`}
                className="rounded px-1 leading-none text-jf-dim hover:bg-red-500/10 hover:text-red-300"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <datalist id={`${ID}-sugestoes`}>
        {sugestoes
          .filter((s) => !escolhidas.has(s.toLocaleLowerCase('pt-BR')))
          .map((s) => (
            <option key={s} value={s} />
          ))}
      </datalist>
      <input
        id={ID}
        type="text"
        list={`${ID}-sugestoes`}
        value={digitada}
        onChange={(e) => onDigitar(e.target.value)}
        onKeyDown={aoTeclar}
        aria-invalid={erro ? true : undefined}
        aria-describedby={erro ? `${ID}-erro` : `${ID}-dica`}
        className="mt-1 w-full rounded-md border border-jf-border bg-jf-bg px-2 py-1.5 text-xs text-white focus:border-jf-yellow focus:outline-none"
      />
      {erro ? (
        <p id={`${ID}-erro`} className="mt-0.5 text-[11px] text-red-400">{erro}</p>
      ) : (
        <p id={`${ID}-dica`} className="mt-0.5 text-[10px] text-jf-dim">
          Escolha uma existente ou digite uma nova e tecle Enter. Funções que começam com &quot;RT&quot; aparecem na Cobertura Técnica.
        </p>
      )}
    </div>
  );
}
